function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React from 'react';
import PropTypes from 'prop-types';
import Button from '../Button';
import Icon from '../Icon';
import Player from '../Player';
import { classNamesFor } from '../utils/classNames';
var cx;
/**
 * :construction: Currently under construction :construction_worker:
 *
 * The MiniProfile component shows a preview of the players profile within the context
 * of other sections of an SIE application. It allows for quick actions allowing users
 * to stay where they are while also allowing interactions with specific players
 *
 * ## Related Components
 * - [AvatarIcon](../AvatarIcon/README.md)
 * - [AvatarIconGroup](../AvatarIconGroup/README.md)
 * - [Player](../Player/README.md)
 *
 * ## Composed of
 * - [Player](../Player/README.md)
 * - [Button](../Button/README.md)
 * - [Icon](../Icon/README.md)
 *
 * ## Usage
 *
 * ```jsx
 * <MiniProfile
 *    accountId="1234"
 *    onlineId="mepsn"
 *    image={(
 *      <AvatarIcon
 *        src="https://avatars/profile/1.webp"
 *        alt="mepsn's avatar image"
 *        withAlert
 *      />
 *    )}
 *    trophyLevel={10}
 *    currentActivityPlatform="ps4"
 *    currentActivityTitle="God of War"
 *    isOfficiallyVerified
 *    isPsPlusMember
 * />
 *```
 *
 * ## Props
 */

var MiniProfile = function MiniProfile(props) {
  var qa = props.qa,
      className = props.className,
      onInteract = props.onInteract;

  var _MiniProfile$getClass = MiniProfile.getClassName({
    className: className
  }),
      root = _MiniProfile$getClass.root,
      player = _MiniProfile$getClass.player,
      ctas = _MiniProfile$getClass.ctas,
      ctaGroup = _MiniProfile$getClass.ctaGroup;

  return /*#__PURE__*/React.createElement("span", {
    "data-qa": qa,
    className: root
  }, /*#__PURE__*/React.createElement(Player, _extends({}, props, {
    qa: "".concat(qa, "#player"),
    className: player,
    size: "large"
  })), /*#__PURE__*/React.createElement("div", {
    className: ctas
  }, /*#__PURE__*/React.createElement(Button, {
    qa: "".concat(qa, "#goToProfile"),
    value: "goToProfile",
    ctaType: "secondary",
    variant: "outline",
    onClick: onInteract
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "ps-user"
  }), /*#__PURE__*/React.createElement("span", null, "Profile")), /*#__PURE__*/React.createElement(Button, {
    qa: "".concat(qa, "#sendMessage"),
    value: "sendMessage",
    ctaType: "secondary",
    variant: "outline",
    onClick: onInteract
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "messages"
  }), /*#__PURE__*/React.createElement("span", null, "Chat")), /*#__PURE__*/React.createElement("div", {
    className: ctaGroup
  }, /*#__PURE__*/React.createElement(Button, {
    qa: "".concat(qa, "#friends?"),
    value: "friends?",
    ctaType: "secondary",
    variant: "outline",
    onClick: onInteract
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "friends"
  }), /*#__PURE__*/React.createElement("span", null, "Friends")), /*#__PURE__*/React.createElement(Button, {
    qa: "".concat(qa, "#more"),
    value: "more",
    ctaType: "secondary",
    variant: "outline",
    onClick: onInteract,
    iconOnly: true
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "chevron-down"
  })))));
};

MiniProfile.getClassName = function (_ref) {
  var className = _ref.className;
  return {
    root: cx('psw-mini-profile', 'psw-l-stack-left', className),
    player: cx('psw-m-b-xs'),
    ctas: cx('psw-mini-profile-ctas', 'psw-l-space-x-xs', 'psw-l-line-left'),
    ctaGroup: cx('psw-border-group-x')
  };
};

MiniProfile.useClassNames = function (classNames) {
  MiniProfile.classNames = classNames;
};

MiniProfile.defaultProps = {
  qa: '',
  className: '',
  name: '',
  onlineId: '',
  accountId: '',
  isOfficiallyVerified: false,
  isPsPlusMember: false,
  image: null,
  trophyLevel: 1,
  currentActivityPlatform: '',
  currentActivityTitle: '',
  onInteract: function onInteract() {}
}; // TODO: figure out how to re-use propTypes that works with react-docgen :(

MiniProfile.propTypes = {
  /**
   * QA selector attribute used to target important elements for test validation
   */
  qa: PropTypes.string,

  /**
   * Allows adding a custom class or series of classes on the component
   * useful for setting margin or padding
   */
  className: PropTypes.string,

  /**
   * The players real name (if user is real name friend)
   */
  name: PropTypes.string,

  /**
   * The players latest onlineId (always available for display)
   */
  onlineId: PropTypes.string,

  /**
   * The unique id associated with the PSN users account
   */
  accountId: PropTypes.string,

  /**
   * Whether the user is a verified user or not. Verified users are users who are
   * - Game developers
   * - Game publishers
   * - Other VIP ;)
   */
  isOfficiallyVerified: PropTypes.bool,

  /**
   * Whether the user is a PS Plus subscriber
   */
  isPsPlusMember: PropTypes.bool,

  /**
   * The players profile image or avatar passed in as an AvatarIcon
   */
  image: PropTypes.node,

  /**
   * The players PSN trophy level (number greater than or equal to 1). Represents
   * there experience with the console
   */
  trophyLevel: PropTypes.number,

  /**
   * "Now Playing" what the player is currently playing
   * The platform being used
   * [TODO: add all platforms]
   */
  currentActivityPlatform: PropTypes.oneOf(['', 'ps4']),

  /**
   * Name of the game being played
   */
  currentActivityTitle: PropTypes.string,

  /**
   * The event callback for indicating a users interaction with the mini-profile
   */
  onInteract: PropTypes.func
};
cx = classNamesFor(MiniProfile);
export default MiniProfile;